import React, { useState, useEffect, useCallback } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import Moment from 'react-moment';
import moment from "moment";
import 'moment/locale/fr';
import { default as BookCalendar } from "../../routes/modules/Calendar";
import { getCompletedRentals } from "../../data/loca/rentals";
import { getEvents } from "../../data/loca/events";
import { getDeliveryVehicleCalendarEvents } from "../../data/loca/deliveryVehicle";
import { getAccount, getUserSettings } from "../../data/accounts";
import { calendarStrings } from '../../services/calendarStrings';

export async function loader() {
    const today = new Date();
    const [othersEvent, settings, account, vehicleEvents] = await Promise.all([
        getEvents(),
        getUserSettings(),
        getAccount().catch(() => null),
        // Indisponibilités du camion : facultatif, l'agenda s'affiche même si l'appel échoue.
        getDeliveryVehicleCalendarEvents(
            new Date(today.getFullYear(), today.getMonth(), today.getDate() - 60),
            new Date(today.getFullYear(), today.getMonth(), today.getDate() + 365)
        ).catch(() => [])
    ]);

    return {
        othersEvent: Array.isArray(othersEvent) ? othersEvent : [],
        vehicleEvents: Array.isArray(vehicleEvents) ? vehicleEvents : [],
        settings,
        account
    };
}

const locaStep = 10;

const readActiveRentals = () => {
    try {
        const raw = localStorage.getItem("activeRentals");
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch (error) {
        return [];
    }
};

const formatEuro = (cents) => (Number(cents ?? 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const statusLabels = {
    Done: "Terminée",
    Cancelled: "Annulée"
};

export default function Index() {
    const { othersEvent, vehicleEvents, settings, account } = useLoaderData();
    const [completed, setCompleted] = useState([]);
    const [completedStart, setCompletedStart] = useState(0);
    const [loadingCompleted, setLoadingCompleted] = useState(false);
    const [hasMoreCompleted, setHasMoreCompleted] = useState(true);
    const [rentalSearch, setRentalSearch] = useState("");
    const [rentalStatusFilter, setRentalStatusFilter] = useState("all");
    const [activeRentals, setActiveRentals] = useState(readActiveRentals);

    // Les locations actives sont chargées par la coquille (root) et diffusées
    // via localStorage + événement : on s'y abonne pour les indicateurs.
    useEffect(() => {
        const refresh = () => setActiveRentals(readActiveRentals());
        window.addEventListener("active-rentals-updated", refresh);
        return () => window.removeEventListener("active-rentals-updated", refresh);
    }, []);

    const toLocalDate = (value) => {
        if (!value) return value;
        if (value instanceof Date) return new Date(value.getTime());
        const parsed = moment.parseZone(value);
        if (!parsed.isValid()) return new Date(value);
        return new Date(
            parsed.year(),
            parsed.month(),
            parsed.date(),
            parsed.hour(),
            parsed.minute(),
            parsed.second(),
            parsed.millisecond(),
        );
    };

    const appointments = othersEvent.map((event) => ({
        id: event.id,
        title: event.title,
        notes: event.notes ?? event.Notes,
        type: "others",
        color: "#4C566A",
        startDate: toLocalDate(event.startDate),
        endDate: toLocalDate(event.endDate)
    })).concat(vehicleEvents.map((event) => ({
        // Récupération / restitution du camion (indisponibilité du véhicule de livraison).
        id: event.id,
        title: event.title,
        notes: event.note,
        type: "vehicle",
        color: event.kind === "pickup" ? "#b45309" : "#7c4a03",
        link: "/outils/vehicule-livraison",
        linkLabel: "Voir le planning du véhicule",
        allDay: event.allDay,
        startDate: toLocalDate(event.start),
        endDate: toLocalDate(event.end)
    })));

    const loadCompletedRentals = useCallback((start = 0, append = false) => {
        setLoadingCompleted(true);
        getCompletedRentals(start, locaStep).then(data => {
            const rentals = Array.isArray(data) ? data : [];
            setCompleted(current => append ? [...current, ...rentals] : rentals);
            setCompletedStart(start + rentals.length);
            setHasMoreCompleted(rentals.length === locaStep);
        }).catch(() => {
            setHasMoreCompleted(false);
        }).finally(() => setLoadingCompleted(false));
    }, []);

    useEffect(() => {
        loadCompletedRentals(0, false);
    }, [loadCompletedRentals]);

    const ongoingCount = activeRentals.filter((rental) => rental.status === "InProgress").length;
    const draftCount = activeRentals.filter((rental) => rental.status === "Draft").length;
    const upcomingCount = activeRentals.length - ongoingCount - draftCount;
    const attentionCount = activeRentals.filter((rental) => rental.requiresAttention).length;

    const firstname = account?.firstname;
    const today = moment().locale("fr").format("dddd D MMMM YYYY");
    const todayLabel = today.charAt(0).toUpperCase() + today.slice(1);

    const clientBody = (rowData) => {
        const name = [rowData.clientFirstname, rowData.clientLastname].filter(Boolean).join(" ") || rowData.clientName;
        return name || <span className="lk-muted">—</span>;
    };

    const actionBody = (rowData) => (
        <Link className="lk-table-link" to={`/${rowData.id}`}>
            Détails <i className="pi pi-angle-right" aria-hidden="true"></i>
        </Link>
    );

    const endDateBody = (rowData) => (
        <Moment locale="fr" calendar={calendarStrings}>
            {new Date(rowData.endDate)}
        </Moment>
    );

    const totalBody = (rowData) => <span className="lk-amount">{formatEuro(rowData.totalRentalAmountCents)}</span>;

    const statusBody = (rowData) => (
        <span className={`lk-status ${rowData.status === "Done" ? "lk-status--done" : rowData.status === "Cancelled" ? "lk-status--cancelled" : ""}`}>
            {statusLabels[rowData.status] || rowData.status}
        </span>
    );

    const statusOptions = [
        { label: "Toutes", value: "all" },
        { label: "Terminées", value: "Done" },
        { label: "Annulées", value: "Cancelled" }
    ];

    const normalizeFilterValue = (value) => (value ?? "").toString().toLowerCase().trim();
    const normalizedSearch = normalizeFilterValue(rentalSearch);
    const filteredCompleted = completed.filter(rental => {
        const matchesStatus = rentalStatusFilter === "all" || rental.status === rentalStatusFilter;
        if (!matchesStatus) {
            return false;
        }

        if (!normalizedSearch) {
            return true;
        }

        const searchableContent = [
            rental.toolName,
            rental.clientFirstname,
            rental.clientLastname,
            rental.clientName,
            rental.status
        ].map(normalizeFilterValue).join(" ");

        return searchableContent.includes(normalizedSearch);
    });

    return (
        <div className="lk-page lk-dash">
            <header className="lk-page-header">
                <div className="lk-page-header__title">
                    <span className="lk-page-header__icon"><i className="pi pi-home" aria-hidden="true"></i></span>
                    <div>
                        <p className="lk-kicker">Tableau de bord</p>
                        <h1>{firstname ? `Bonjour ${firstname}` : "Vos réservations"}</h1>
                        <p className="lk-page-header__subtitle">{todayLabel}</p>
                    </div>
                </div>
                <div className="lk-page-header__actions">
                    <Link to="/ajouter" className="lk-btn lk-btn--primary">
                        <i className="pi pi-plus" aria-hidden="true"></i>
                        Nouvelle réservation
                    </Link>
                </div>
            </header>

            <div className="lk-kpis" aria-label="Indicateurs">
                <div className="lk-kpi">
                    <span className="lk-kpi__icon"><i className="pi pi-clock" aria-hidden="true"></i></span>
                    <div>
                        <span className="lk-kpi__value">{ongoingCount}</span>
                        <span className="lk-kpi__label">{ongoingCount > 1 ? "Locations en cours" : "Location en cours"}</span>
                    </div>
                </div>
                <div className="lk-kpi">
                    <span className="lk-kpi__icon"><i className="pi pi-calendar-plus" aria-hidden="true"></i></span>
                    <div>
                        <span className="lk-kpi__value">{upcomingCount}</span>
                        <span className="lk-kpi__label">{upcomingCount > 1 ? "Locations à venir" : "Location à venir"}</span>
                    </div>
                </div>
                <div className={`lk-kpi${draftCount > 0 ? " lk-kpi--warning" : ""}`}>
                    <span className="lk-kpi__icon"><i className="pi pi-pencil" aria-hidden="true"></i></span>
                    <div>
                        <span className="lk-kpi__value">{draftCount}</span>
                        <span className="lk-kpi__label">À compléter</span>
                    </div>
                </div>
                <div className={`lk-kpi${attentionCount > 0 ? " lk-kpi--warning" : ""}`}>
                    <span className="lk-kpi__icon"><i className="pi pi-exclamation-circle" aria-hidden="true"></i></span>
                    <div>
                        <span className="lk-kpi__value">{attentionCount}</span>
                        <span className="lk-kpi__label">{attentionCount > 1 ? "Actions attendues" : "Action attendue"}</span>
                    </div>
                </div>
            </div>

            <section className="lk-card lk-calendar">
                <div className="lk-card__header">
                    <div>
                        <h2 className="lk-card__title">Vos réservations à venir</h2>
                        <p className="lk-card__subtitle">Agenda des locations, livraisons, reprises, du camion et de vos propres événements.</p>
                    </div>
                </div>
                <BookCalendar
                    events={appointments}
                    rentalEventDurationMinutes={settings?.rentalCalendarEventDurationMinutes}
                    rentalDeliveryEventDurationMinutes={settings?.rentalDeliveryCalendarEventDurationMinutes}
                    rentalDeliveryWithSetupEventDurationMinutes={settings?.rentalDeliveryWithSetupCalendarEventDurationMinutes}
                    rentalPickupEventDurationMinutes={settings?.rentalPickupCalendarEventDurationMinutes}
                />
            </section>

            <section className="lk-card lk-dash-table">
                <div className="lk-card__header">
                    <div>
                        <h2 className="lk-card__title">Dernières locations</h2>
                        <p className="lk-card__subtitle">Historique des locations terminées ou annulées.</p>
                    </div>
                </div>

                {completed.length > 0 ? (
                    <>
                        <div className="home-rentals-filters">
                            <div className="home-rentals-filter home-rentals-filter-search">
                                <label htmlFor="completed-rental-search">Recherche</label>
                                <span className="p-input-icon-left">
                                    <i className="pi pi-search" />
                                    <InputText
                                        id="completed-rental-search"
                                        value={rentalSearch}
                                        onChange={(e) => setRentalSearch(e.target.value)}
                                        placeholder="Outil ou client"
                                    />
                                </span>
                            </div>
                            <div className="home-rentals-filter home-rentals-filter-status">
                                <label htmlFor="completed-rental-status">État</label>
                                <Dropdown
                                    inputId="completed-rental-status"
                                    value={rentalStatusFilter}
                                    options={statusOptions}
                                    onChange={(e) => setRentalStatusFilter(e.value)}
                                    placeholder="Toutes"
                                />
                            </div>
                        </div>

                        <DataTable value={filteredCompleted} emptyMessage="Aucune location ne correspond au filtre">
                            <Column field="toolName" header="Outil"></Column>
                            <Column body={clientBody} header="Client"></Column>
                            <Column body={statusBody} header="État"></Column>
                            <Column header="Date de fin" body={endDateBody} />
                            <Column body={totalBody} header="Total"></Column>
                            <Column header="" body={actionBody} />
                        </DataTable>

                        {hasMoreCompleted && (
                            <div className="lk-load-more">
                                <Button
                                    type="button"
                                    label="Voir plus"
                                    icon="pi pi-plus"
                                    loading={loadingCompleted}
                                    onClick={() => loadCompletedRentals(completedStart, true)}
                                    outlined
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="lk-empty">
                        <i className={loadingCompleted ? "pi pi-spin pi-spinner" : "pi pi-inbox"} aria-hidden="true"></i>
                        <strong>{loadingCompleted ? "Chargement de l'historique..." : "Aucune location terminée pour le moment"}</strong>
                        {!loadingCompleted && <span>Vos locations terminées ou annulées apparaîtront ici.</span>}
                    </div>
                )}
            </section>
        </div>
    );
}
